import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationButton from './NotificationButton';
import { API_URL } from '../config';
import '../styles/Header.css';

interface Suggestion {
  id: string;
  name: string;
  type: 'sitter' | 'merch';
}

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTerm.length > 1) {
      fetch(`${API_URL}/api/search?q=${encodeURIComponent(searchTerm)}`)
        .then(res => res.json())
        .then(data => {
          const sitterSugs = data.sitters.map((s: any) => ({ id: s.id, name: s.name, type: 'sitter' }));
          // const merchSugs = data.merchandise.map((m: any) => ({ id: m.id, name: m.name, type: 'merch' }));
          setSuggestions([...sitterSugs].slice(0, 8));
          setShowSuggestions(true);
        })
        .catch(err => console.error('Suggestion error:', err));
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchTerm]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
      setSearchTerm('');
      setShowSuggestions(false);
    }
  };

  const handleSelect = (sug: Suggestion) => {
    if (sug.type === 'sitter') navigate(`/app/${sug.id}`);
    // else navigate(`/shopping/${sug.id}`);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  return (
    <>
      <header className="main-header">
        <div className="header-content">
          <Link to="/" className="logo">
            <img src="/logo.png" alt="The F.I.N.D. Logo" className="header-logo-img" />
          </Link>
          
          <div className="search-container" ref={searchRef} style={{ flex: 1, maxWidth: '400px', position: 'relative' }}>
            <form className="search-bar" onSubmit={handleSearch} style={{ width: '100%', position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }}>🔍</span>
              <input 
                type="text" 
                placeholder="Search sitters..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                style={{ paddingLeft: '35px', paddingRight: searchTerm ? '35px' : '12px' }}
              />
              {searchTerm && (
                <button 
                  type="button" 
                  onClick={() => setSearchTerm('')}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '1.1rem' }}
                >
                  ×
                </button>
              )}
            </form>

            {showSuggestions && suggestions.length > 0 && (
              <ul className="search-suggestions" style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                listStyle: 'none',
                padding: '8px 0',
                margin: '5px 0 0 0',
                zIndex: 3000,
                border: '1px solid #eee'
              }}>
                {suggestions.map((sug, i) => (
                  <li 
                    key={i} 
                    onClick={() => handleSelect(sug)}
                    style={{
                      padding: '10px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.9rem',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                  >
                    <span style={{ fontWeight: '500', color: '#333' }}>{sug.name}</span>
                    <span style={{ 
                      fontSize: '0.7rem', 
                      backgroundColor: sug.type === 'sitter' ? '#e3f2fd' : '#f0f4f8',
                      color: sug.type === 'sitter' ? '#1976d2' : '#666',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      textTransform: 'uppercase'
                    }}>
                      {sug.type === 'merch' ? 'Item' : sug.type}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <nav className="nav-links">
            <Link to="/">
              <span>🔍</span>
              <span className="mobile-hide" style={{ marginLeft: '5px' }}>Discover</span>
            </Link>
            <Link to="/categories">
              <span>📚</span>
              <span className="mobile-hide" style={{ marginLeft: '5px' }}>Categories</span>
            </Link>
            <Link to="/events">
              <span>📅</span>
              <span className="mobile-hide" style={{ marginLeft: '5px' }}>Events</span>
            </Link>
            {user?.id.startsWith('sitter-') && (
              <Link to="/shopping">
                <span>🛒</span>
                <span className="mobile-hide" style={{ marginLeft: '5px' }}>Shopping</span>
              </Link>
            )}
            
            {user && (
              <Link to="/dashboard" className="sitter-link">
                <span>👶</span>
                <span className="mobile-hide" style={{ marginLeft: '5px' }}>My Bookings</span>
              </Link>
            )}
            
            <Link to="/contact">
              <span>💬</span>
              <span className="mobile-hide" style={{ marginLeft: '5px' }}>Contact</span>
            </Link>
            
            {user ? (
              <div className="user-nav">
                <NotificationButton />
                <Link 
                  to="/avatar-creator" 
                  className="welcome-text"
                >
                  {user.avatar && <img src={user.avatar} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #ddd' }} />}
                  <span className="mobile-hide" style={{ marginLeft: '5px' }}>Hi, {user.name.split(' ')[0]}!</span>
                </Link>
                <button onClick={logout} className="logout-btn">
                  <span className="mobile-hide">Logout</span>
                  <span className="desktop-hide">👋 Bye</span>
                </button>
              </div>
            ) : (
              <>
                <Link to="/login">Login</Link>
                <Link to="/signup" className="signup-btn">Sign Up</Link>
              </>
            )}
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;
